import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import { isArrayOfString } from '../util/general';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import type { ClientHookContextData} from './clientHooks';
import { isInstanceOfClientHookContextData } from './clientHooks';
import type { TimedMdfUsageRule } from './timedMdfUsage';
import type { FieldValue, ItemMetaData, TechnicalMetadata } from './fieldValue';
import { type Alternative, type AlternativeValue, FieldType, type MdfFieldEntity } from '../mdf/fieldDefinition';

export interface MdfCreationRequest {
  label: string;
  active: boolean;
  color?: string;
  flavor?: MdfFlavor;
  isSubtype?: boolean;
  displayName?: string;
}

/**
 * Settings interface that allow users to customize the look
 * and feel for a field.
 */
export interface LayoutSettings {
  id: string;
  fieldId: string; // Used to keep in sync which field this belongs to
  label: string;
  visible: boolean;
  flex?: number;
  icon?: string;
  hint?: string;
  showAlways?: boolean;

  // Only relevant for some fields.
  fieldHeight?: number;
  maxLineHeight?: number;
  multiline?: boolean;
  // Only relevant to File type fields for now.
  message?: string;
  // Store color of alternatives for choice, multiple-choice & checkbox fields.
  colors?: Record<string, string>;
  // Store display values for checkbox fields
  displayValues?: Record<string, string>;
  // mdf label to which the field belongs
  mdfSource?: string;
  // View specific default value.
  // The priority order is view-defaultValue > model-defaultValue > nothing.
  defaultValue?: FieldValue;
}

/**
 * LayoutSection is used to visually separate fields in the UI.
 * It is not used in the model itself.
 */
export interface LayoutSection {
  sectionId: string; // anchor to link to
  label: string;
  placeAboveFieldId?: string | null; // Used to place section above a field
  sortOrder?: number; // Used to order multiple sections above the same field
  defaultCollapsed?: boolean; // default collapsed state
  icon?: string;
}

/**
 * LayoutSection type guard
 */
export function isLayoutSection(v: unknown): v is LayoutSection {
  if (!isObject(v)) return false;
  const section = v as Partial<LayoutSection>;
  return typeof section.sectionId === 'string' &&
         typeof section.label === 'string' &&
         (section.placeAboveFieldId === undefined || typeof section.placeAboveFieldId === 'string' || section.placeAboveFieldId === null);
}

/**
 * The current supported ViewType's that we save a LayoutSettings object per field for.
 */
export enum ViewType {
  /**
   * Used in the advanced search menu
   */
  SEARCH = 'search',
  /**
   * Used in the item details page
   */
  ITEM = 'item',
  /**
   * Used in metadata form editing
   */
  EDITING = 'editing',
  /**
   * Used in share page
   */
  SHARE = 'share',
  /**
   * Used in upload dialogs
   */
  UPLOAD = 'upload',
  /**
   * Used in scheduler dialogs
   */
  SCHEDULER = 'scheduler',
  /**
   * Used in copy item dialogs
   */
  COPY_ITEM='copyItem',
  /**
   * Used in create new version dialog
   */
  NEW_VERSION='newVersion',
  /**
   * Used in create placeholder dialogs
   */
  CREATE_PLACEHOLDER='createPlaceholder',
  /**
   * Used in the folder metadata preview
   */
  FOLDER='folder',
}

/**
 * The metadata view types where we allow to edit item's metadata
 * this function is used in various places to check whether to
 * sanitize the metadata values or apply various field properties etc
 */
export const isMetadataEditableViewType = (viewType: ViewType) => {
  return (
    viewType === ViewType.ITEM ||
    viewType === ViewType.UPLOAD ||
    viewType === ViewType.SCHEDULER ||
    viewType === ViewType.COPY_ITEM ||
    viewType === ViewType.NEW_VERSION ||
    viewType === ViewType.CREATE_PLACEHOLDER ||
    viewType === ViewType.SHARE ||
    viewType === ViewType.FOLDER
  );
};

/**
 * Button fields should be visible in all views except search
 */
export const isButtonFieldVisibleViewType = (viewType: ViewType) => {
  return viewType !== ViewType.SEARCH;
};

/**
 * File fields should be visible in upload/item view only
 */
export const isFileFieldVisibleViewType = (viewType: ViewType) => {
  return viewType === ViewType.UPLOAD || viewType === ViewType.ITEM;
};

/**
 * This forces anyone adding new ViewTypes to implement it across the codebase.
 */
export type Views = {
  [V in ViewType]: LayoutSettings[];
};

export type ViewSections = {
  [ViewType.ITEM]: LayoutSection[] | undefined;
};

export function isAlternativeArray(v: unknown): v is Alternative[] {
  return Array.isArray(v) && v.every(isAlternative);
}

function isAlternative(v: unknown): v is Alternative {
  if (!isObject(v)) return false;
  const alt = v as Partial<Alternative>;
  return typeof alt.label === 'string' && isAlternativeValue(alt.value);
}

/**
 * Alternative[] is for regular choice or multi-choice fields' options
 * string[][] is for Tree choice fields' options
 */
export type AlternativesType = Alternative[] | string[][];

export function isAlternativeValue(v: unknown): v is AlternativeValue {
  const t = typeof v;
  return t === 'string' || t === 'number' || t === 'boolean';
}

export const fieldTypeLabels: Record<FieldType, string> = {
  [FieldType.Text]: 'Text',
  [FieldType.Checkbox]: 'Checkbox',
  [FieldType.Date]: 'Date',
  [FieldType.Number]: 'Number',
  [FieldType.Choice]: 'Choice',
  [FieldType.MultipleChoice]: 'Multiple choice',
  [FieldType.Link]: 'Link',
  [FieldType.User]: 'User',
  [FieldType.File]: 'File',
  [FieldType.Button]: 'Button',
  [FieldType.JSON]: 'JSON',
  [FieldType.SubType]: 'Sub Type',
  [FieldType.TreeChoice]: 'Tree Choice'
};

export interface FieldError {
  errors: string[]; // internal field id
  label: string; // field label
  icon?: string;
  isExternalError?: boolean; // Errors that are generated from external validation systems like client hooks
}

export type MetadataErrors = Record<string, FieldError>;
export type ButtonStates = Record<string, ButtonState>;


/**
 * Metadata schema's as they are saved in the database.
 *
 * FIXME: We should define one type for what is stored in the database,
 * and one type for what is sent over the wire.
 */
export interface MdfEntity {
  id: string;

  // User defined label for the schema.
  // The label must be unique across the tenant.
  // We accept all characters except quotes and period, which are disallowed
  // to simplify the search code.
  label: string;

  active: boolean;
  icon?: string;
  color?: string;
  compatibleWithPremiereMarker?: boolean;

  // This field is the template used to generate a description for a
  // payload instance. It can parameterized by using '{xyz}' markers
  // that will be replaced by the values of the fields with these names.
  // For instance, if the model defines a field 'foo' that is a boolean
  // and a field 'bar' that is text, the generated description for
  // the rule 'BAR:{bar}={foo}' will be 'BAR:sometext=true' for a
  // payload whose value for 'bar' and 'foo' are respectively 'sometext'
  // and true.
  // Markers cannot be nested, cannot be empty and any unrecognized field
  // marker will be left as is like this:
  // 'BAR:{bar}={foo}/{unknown}' => 'BAR:sometext=true/{unknown}'
  rule?: string;

  views: Views | undefined;

  // sections for views that should not be mixed with the view layout settings
  viewSections: ViewSections | undefined;

  fields?: MdfFieldEntity[];

  // The flavor is a string used to characterize a subset of mdf models.
  // The undefined flavor is used to select the mdf models used for regular
  // metadata models.
  // The 'timed-metadata' flavor is used to select the mdf models used for timed
  // metadata.
  flavor?: MdfFlavor;

  // This field is specific to the Restricted Material schema:
  // only that schema will have this field defined.
  usageRules?: TimedMdfUsageRule[];

  permissions?: FieldPermissions;

  // To which groups the mdf type should be shown in the mdf type dropdown list (client only check)
  // undefined means the mdf type is visible to all groups
  visibleTo?: string[];

  // Optional field mappings to enable reusing fields instead of dynamically adding
  // one new field mapping per MDF field.
  // For now, this property will only be used for timed events.
  isSubtype?: boolean;

  // last modified on date ISO string, only implicitly changed by the mapper code
  modifiedOn?: string;

  // For timed mdf only: Keyboard shortcut for logging events
  keyboardShortcut?: string;

  // For timed mdf only: The default duration the client should use for log events of this type.
  duration?: number;

  // Editable name for UI, when present it will be shown in mdf dropdown instead of label property.
  displayName?: string;
}

/**
 * A subset of MdfEntity that most importantly does not include views.
 * See MdfEntity for field descriptions.
 *
 * *NOTE* You cannot simply add fields to this type in the usual way:
 * MdfSchemaNoViews must be compatible with the projected fields of
 * the  mdf table's 'tenant-no-views' index.
 * If adding new fields is needed, one option is to first migrate
 * to a new gsi that does have the new field(s) projected.
 */
export interface MdfSchemaNoViews {
  id: string;
  label: string;
  active: boolean;
  icon?: string;
  color?: string;
  compatibleWithPremiereMarker?: boolean;
  rule?: string;
  fields?: MdfFieldEntity[];
  flavor?: MdfFlavor;
  permissions?: FieldPermissions;
  visibleTo?: string[];
  isSubtype?: boolean;
  modifiedOn?: string;
}


export type MdfFlavor =
  'timed-metadata' |
  'technical_data' |
  'graphics_data';

/**
 * Per field read-write permissions. Permissions are defined by a records where the keys are 'fieldId's
 * and the values are arrays of user group identifiers.
 */
export interface FieldPermissions {
  read: Record<string, string[]>;
  write: Record<string, string[]>;
}

export interface MetadataSnapshot {
  latestSnapshot: ItemMetaData;
  metadataVersion: number, // The metadataVersion corresponding to latestSnapshot
  conflictedFields: FieldValueDiff[];
}

const validationTypes = <const>['field_validation', 'full_validation'];

export type MdfValidationPayload = Pick<ItemMetaData, 'formId' | 'formData'> & ClientHookContextData & {
  userId: string;
  groups: string[];
  type: typeof validationTypes[number];
  // Only included when an MDF button has been pressed
  triggerFieldId?: string | undefined;
  // 'technicalMetadata' currently used for 'metadata tab' of item-details and quick-preview.
  technicalMetadata: TechnicalMetadata | undefined;
};

export function isInstanceOfMdfValidationPayload(obj: unknown): obj is MdfValidationPayload {
  if (!isInstanceOfClientHookContextData(obj)) return false;
  const payload = obj as Partial<MdfValidationPayload>;

  return isString(payload.formId) && isObject(payload.formData)
    && isString(payload.userId) && isArrayOfString(payload.groups)
    && isString(payload.type) && validationTypes.includes(payload.type)
    && (isString(payload.triggerFieldId) || isUndefined(payload.triggerFieldId))
    && (isInstanceOfTechnicalMetadata(payload.technicalMetadata) || isUndefined(payload.technicalMetadata));
}


export type MdfValidationMessageStatus = 'success' | 'error' | 'warning';

export interface MdfValidationMessage {
  general?: string;
  status?: MdfValidationMessageStatus;
  perField?: Record<string, string>;
}

/**
 * Note that formId returned by API may contain a reference to the MDF Label instead of the id.
 * more details: https://github.com/Mjoll/mimirdocs/blob/master/ClientHookAPI.md#expected-response-payload
 */
export interface MdfValidationResponse extends ItemMetaData {
  message?: MdfValidationMessage;
  doSaveMetadata?: boolean;
  canSave?: boolean;
  isActionAllowed?: boolean;
  buttonStates?: ButtonStates
}

export interface ButtonState {
  disabled: boolean,
  tooltip?: string
}

export const isInstanceOfItemMetadata = (obj: unknown): obj is ItemMetaData => {
  if (!isObject(obj)) return false;
  const metadata = obj as Partial<ItemMetaData>;
  return !!metadata.formId && !!metadata.formData;
};

export interface MdfDiff {
  oldMdfId: string | undefined;
  newMdfId: string | undefined;
  metadataFields: FieldValueDiff[];
  startTime?: TimeDurationValueDiff;
  duration?: TimeDurationValueDiff;
}

export interface MdfDiffDelta {
  oldMdfId: string | undefined;
  newMdfId: string | undefined;
  metadataFields: FieldValueDiffWithNull[];
  startTime?: TimeDurationValueDiff;
  duration?: TimeDurationValueDiff;
}
export interface MdfDiffStrict {
  oldMdfId: string;
  newMdfId: string;
  metadataFields: FieldValueDiff[];
}

export interface TimeDurationValueDiff {
  oldValue: number | undefined;
  newValue: number | undefined;
}

export interface FieldValueDiff {
  fieldId: string;
  oldValue: FieldValue | undefined;
  newValue: FieldValue | undefined;
}

/**
 * This is used in mdf bulk update in the client where we assign values to null to clear fields
 * FIXME: This type uses both undefined and null to represent the absence of a value, which
 * should not be needed. See comments on #11712 for discussion.
 */
export interface FieldValueDiffWithNull {
  fieldId: string;
  oldValue: FieldValue | undefined | null;
  newValue: FieldValue | undefined | null;
}

/**
 * Client representation of treeChoice field.
 */
export interface TreeChoiceNode {
  label: string;
  children: TreeChoiceNode[];
  parent: TreeChoiceNode | null;
}

export const isInstanceOfTechnicalMetadata = (obj: unknown): obj is TechnicalMetadata => {
  return isInstanceOfItemMetadata(obj);
};

export const isMdfCreationRequest = (obj: unknown): obj is MdfCreationRequest => {
  if (!isObject(obj)) return false;
  const probablyCreationRequest = obj as Partial<MdfCreationRequest>;
  return probablyCreationRequest.label !== undefined && probablyCreationRequest.active !== undefined;
};

export const isItemMetaData = (obj: unknown): obj is ItemMetaData => {
  return isObject(obj) && Object.keys(obj).length === 2 && typeof obj.formId === 'string' && isObject(obj.formData);
};

export function getValidatedMetadata(obj: unknown): { blocker: string; metadata?: undefined } | { blocker?: undefined; metadata: ItemMetaData } {
  if (!isObject(obj)) return { blocker: 'Not a valid object' };

  const metadata = obj as Partial<ItemMetaData>;

  if (typeof metadata.formId !== 'string') return { blocker: 'formId is not a string' };
  if (!isObject(metadata.formData)) return { blocker: 'formData is not a valid object' };

  return {
    metadata: {
      formId: metadata.formId,
      formData: metadata.formData,
    },
  };
}


/**
 * Defines the payload used to partially update an item's metadata using the 'metadataDelta' property.
 * Unlike full metadata, this payload allows null values in the formData object. Null values can be used
 * to remove specific fields from the metadata of an item.
 */
export interface MetadataDeltaPayload {
  formId: string;
  formData: Record<string, FieldValue | null>;
}

export function getEmptyMdfViewsObject(): Views {
  return {
    [ViewType.SEARCH]: [],
    [ViewType.ITEM]: [],
    [ViewType.EDITING]: [],
    [ViewType.SHARE]: [],
    [ViewType.UPLOAD]: [],
    [ViewType.SCHEDULER]: [],
    [ViewType.COPY_ITEM]: [],
    [ViewType.NEW_VERSION]: [],
    [ViewType.CREATE_PLACEHOLDER]: [],
    [ViewType.FOLDER]: [],
  };
}

export function getEmptyMdfViewSectionsObject(): ViewSections {
  // Currently the only view that has sections is ITEM
  return {
    [ViewType.ITEM]: []
  };
}


export type GraphicsPayloadModel = MdfEntity & { flavor: 'graphics_data', fields: MdfFieldEntity[] };
